import { useState, useEffect, useCallback } from "react";
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  Autocomplete,
  Circle,
} from "@react-google-maps/api";
import { useJobPostProgressContext } from "../../../../context/jobPostProgressFrom";
import { Input } from "../../../../components/ui/input";

const libraries = ["places"];

export function MapComponent({ isInLocation, setIsInLocation, helperLocations }) {
  const [circles, setCircles] = useState(<></>);
  const [helpersHaveBeenSet, setHelpersHaveBeenSet] = useState(helperLocations.length);
  const [position, setPosition] = useState(null); // Current marker position
  const [map, setMap] = useState(null); // Google Map instance
  const [autocomplete, setAutocomplete] = useState(null); // Autocomplete instance
  const [geocoder, setGeocoder] = useState(null); // Geocoder instance
  const { jobPostProgress, setLocation } = useJobPostProgressContext(); // Access context

  if (helpersHaveBeenSet !== helperLocations.length) {
    setHelpersHaveBeenSet(helperLocations.length);
    console.log("Helpers", helperLocations)
    setCircles(helperLocations.map(({long, lat}, i) => {
      console.log(long, lat);
      return (
        <Circle key={i}
          center={{
            lat: lat,
            lng: long,
          }}
          radius={200}
          options={{
            fillColor: "red", // Circle color
            fillOpacity: 0.15, // 25% opacity for the fill
            strokeColor: "red", // Circle border color
            strokeOpacity: 0.5, // 50% opacity for the border
            strokeWeight: 1, // Border thickness
          }}
        />
    );
    }))
  }

  

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: "AIzaSyDJQNAbTK3AGLlmRGVxa3VbejegSp-qB9A",
    libraries,
  });

  const onLoad = (mapInstance) => {
    setMap(mapInstance);
    setGeocoder(new window.google.maps.Geocoder()); // Initialize Geocoder
  };

  const onAutocompleteLoad = (autocompleteInstance) => {
    setAutocomplete(autocompleteInstance);
  };

  const handlePlaceSelect = () => {
    if (autocomplete) {
      const place = autocomplete.getPlace();
      if (place.geometry) {
        const { lat, lng } = place.geometry.location;
        setPosition({ lat: lat(), lng: lng() });
        map.panTo({ lat: lat(), lng: lng() });

        // Update context with new address and coordinates
        if (place.formatted_address) {
          setLocation(place.formatted_address, lat(), lng());
        }
      }
    }
  };

  // Reverse geocode to get address from lat and lng
  const reverseGeocode = (lat, lng) => {
    if (geocoder) {
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === "OK" && results[0]) {
          const newAddress = results[0].formatted_address;

          // Check if country is Canada
          const countryComponent = results[0].address_components.find(
            (component) => component.types.includes("country")
          );
          const country = countryComponent?.long_name || "";

          setIsInLocation(country === "Canada");
          setLocation(newAddress, lat, lng);
        } else {
          console.error("Geocoder failed due to: " + status);
        }
      });
    }
  };

  // Debounce function to limit how often the center is updated during drag
  const debounce = (func, timeout = 200) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        func.apply(this, args);
      }, timeout);
    };
  };

  // Handle the map's center changing dynamically (while dragging) with debouncing
  const handleMapCenterChanged = useCallback(
    debounce(() => {
      if (map) {
        const newPosition = map.getCenter();
        const newLat = newPosition.lat();
        const newLng = newPosition.lng();

        // Only update the position if it's different from the current one
        if (newLat !== position?.lat || newLng !== position?.lng) {
          setPosition({ lat: newLat, lng: newLng });
          reverseGeocode(newLat, newLng); // Reverse geocode to get the updated address
        }
      }
    }, 300), // Debounce for 300ms
    [map, position, geocoder]
  );

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (location) => {
          const { latitude, longitude } = location.coords;
          setPosition({ lat: latitude, lng: longitude });
          if (map) {
            map.panTo({ lat: latitude, lng: longitude });
            reverseGeocode(latitude, longitude); // Reverse geocode to update address in context
          }
        },
        (error) => {
          console.error("Error getting location", error);
        }
      );
    }
  }, [map]);

  if (!isLoaded) {
    return <div>Loading map...</div>;
  }

  return (
    <div>
      <div className="flex gap-4 items-center mb-2 w-full text-xl tracking-wide rounded-lg">
        <Autocomplete
          onLoad={onAutocompleteLoad}
          onPlaceChanged={handlePlaceSelect}
          className="w-full p-1"
        >
          <Input
            type="text"
            placeholder="Search"
            value={jobPostProgress.location.address} // Bind the input to context state
            onChange={(e) =>
              setLocation(e.target.value, position?.lat, position?.lng)
            } // Update context on input change
            className="bg-gray-100 "
          />
        </Autocomplete>
      </div>
      <GoogleMap
        mapContainerStyle={{ height: "500px", width: "100%", margin: ".25rem" }}
        center={position}
        zoom={13}
        onLoad={onLoad}
        onCenterChanged={handleMapCenterChanged} // Update marker position and address during drag
      >
        {position && (
          <Marker
            position={position}
            label={
              isInLocation
                ? undefined
                : { text: "Not Available", color: "red", fontSize: "12px" }
            }
            icon={
              isInLocation
                ? undefined // default marker
                : {
                    path: window.google.maps.SymbolPath.BACKWARD_CLOSED_ARROW, // cross alternative
                    scale: 5,
                    fillColor: "#FF0000",
                    fillOpacity: 1,
                    strokeWeight: 1,
                    strokeColor: "#000000",
                    anchor: new window.google.maps.Point(0, 5),
                  }
            }
          />
        )}
        {circles}
      </GoogleMap>
    </div>
  );
}